// Real per-model pricing for cost accounting (plan §6). USD per 1,000,000 tokens.
//
// IMPORTANT: these must be kept current with providers' published rates. Exact
// values are the one remaining open item in the plan (§10 "Still open") — verify
// before relying on cost figures. gpt-5-mini is a placeholder pending published
// rates. When the backend owns runs, pricing should ideally live server-side.

import type { Model, TokenUsage } from './types'

export type ModelPricing = { inputPerMillion: number; outputPerMillion: number }

/** GPT-only, USD per 1M tokens. OpenAI has no pricing API, so this is maintained by
 *  hand — verify against current published rates before relying on cost figures (plan
 *  §10). Models absent here (e.g. the gpt-5.1+ tiers, whose rates post-date this table)
 *  fall through to 0 in costForUsage; the backend should own authoritative pricing. */
export const MODEL_PRICING: Record<string, ModelPricing> = {
  'gpt-5': { inputPerMillion: 1.25, outputPerMillion: 10.0 },
  'gpt-5-mini': { inputPerMillion: 0.25, outputPerMillion: 2.0 },
  'gpt-5-nano': { inputPerMillion: 0.05, outputPerMillion: 0.4 },
  'gpt-4.1': { inputPerMillion: 2.0, outputPerMillion: 8.0 },
  'gpt-4.1-mini': { inputPerMillion: 0.4, outputPerMillion: 1.6 },
  'gpt-4.1-nano': { inputPerMillion: 0.1, outputPerMillion: 0.4 },
  'gpt-4o': { inputPerMillion: 2.5, outputPerMillion: 10.0 },
  'gpt-4o-mini': { inputPerMillion: 0.15, outputPerMillion: 0.6 },
  'gpt-4-turbo': { inputPerMillion: 10.0, outputPerMillion: 30.0 },
  'gpt-4': { inputPerMillion: 30.0, outputPerMillion: 60.0 },
  'gpt-3.5-turbo': { inputPerMillion: 0.5, outputPerMillion: 1.5 },
}

/** Cost in USD for one model call's token usage. */
export function costForUsage(model: Model, usage: TokenUsage): number {
  const pricing = MODEL_PRICING[model]
  if (!pricing) return 0
  return (
    (usage.promptTokens / 1_000_000) * pricing.inputPerMillion +
    (usage.completionTokens / 1_000_000) * pricing.outputPerMillion
  )
}

/** Formats a USD cost, using more precision for sub-cent figures. */
export function formatCost(usd: number): string {
  if (usd === 0) return `$0.00`
  if (usd < 0.01) return `$${usd.toFixed(4)}`
  return `$${usd.toFixed(2)}`
}

/** Formats a token count compactly, e.g. `12.3k`. */
export function formatTokens(tokens: number): string {
  if (tokens < 1000) return String(tokens)
  return `${(tokens / 1000).toFixed(1)}k`
}
